import type { ITransportResponse } from '@cubejs-client/core'
import { InstrumentedHttpTransport } from './InstrumentedHttpTransport'

export type CubeRetryConfig = {
  /**
   * Delay (ms) before each retry attempt. The array length equals the number
   * of retries performed after the initial attempt. The default `[2000, 5000]`
   * means: attempt immediately, retry after 2s, then a final retry after 5s
   * (3 attempts total).
   */
  delaysMs: number[]
}

export const DEFAULT_CUBE_RETRY_CONFIG: CubeRetryConfig = {
  delaysMs: [2000, 5000],
}

const RETRYABLE_STATUS = new Set([500, 503, 504])
const RETRYABLE_ERRORS = new Set(['network error', 'timeout'])

// Transient failures worth retrying: 5xx from a cold/overloaded backend and the
// cube client's own network/timeout markers. Deterministic 4xx (e.g. the empty
// `service_area_uuid` 400) are NOT retryable — they fail fast, unchanged.
const isRetryable = (response: unknown): boolean => {
  if (response instanceof Response) {
    return RETRYABLE_STATUS.has(response.status)
  }
  if (
    response &&
    typeof response === 'object' &&
    'error' in response &&
    typeof (response as { error: unknown }).error === 'string'
  ) {
    return RETRYABLE_ERRORS.has(
      (response as { error: string }).error.toLowerCase(),
    )
  }
  return false
}

/**
 * Extends {@link InstrumentedHttpTransport} to transparently retry transient
 * Cube API failures with a fixed backoff schedule. Retries happen below the
 * cube React hook, so a recovering query never surfaces an error/NaN flash — it
 * simply stays in its loading state a little longer.
 */
export class RetryingHttpTransport extends InstrumentedHttpTransport {
  private readonly retryConfig: CubeRetryConfig

  constructor(
    options: ConstructorParameters<typeof InstrumentedHttpTransport>[0],
    retryConfig: CubeRetryConfig = DEFAULT_CUBE_RETRY_CONFIG,
  ) {
    super(options)
    this.retryConfig = retryConfig
  }

  request(
    method: string,
    params: Record<string, unknown>,
  ): ITransportResponse<Response> {
    const instrumented = super.request(method, params)
    const { delaysMs } = this.retryConfig
    // One pending backoff timer per request response, cleared on unsubscribe so
    // a torn-down query is never resubscribed.
    let timer: ReturnType<typeof setTimeout> | undefined

    return {
      subscribe: <CBResult>(
        cb: (
          response: Response | { error: string },
          resubscribe: () => Promise<CBResult>,
        ) => CBResult,
      ): Promise<CBResult> => {
        let attempt = 0

        const handle = (
          response: Response | { error: string },
          resubscribe: () => Promise<CBResult>,
        ): CBResult => {
          if (isRetryable(response) && attempt < delaysMs.length) {
            const delay = delaysMs[attempt]
            attempt += 1
            // In practice the cube client instantiates CBResult as a Promise,
            // so returning a deferred resubscribe from the callback matches its
            // own `continueWait` behavior.
            return new Promise<void>(resolve => {
              timer = setTimeout(resolve, delay)
            }).then(() => resubscribe()) as CBResult
          }
          return cb(response, resubscribe)
        }

        return instrumented.subscribe(handle)
      },
      unsubscribe: async () => {
        if (timer) clearTimeout(timer)
        await instrumented.unsubscribe?.()
      },
    }
  }
}
